import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { createOrder } from '../services/api';
import { usePayment } from '../hooks/usePayment';
import toast from 'react-hot-toast';

const paymentMethods = [
  { value: 'bkash', label: 'bKash',  icon: '📱' },
  { value: 'nagad', label: 'Nagad',  icon: '💳' },
  { value: 'card',  label: 'Card',   icon: '🏦' },
  { value: 'cod',   label: 'Cash on Delivery', icon: '💵' },
];

export default function CheckoutPage() {
  const { cart, cartTotal, shippingFee, clearCart } = useCart();
  const navigate = useNavigate();
  const { payWithBkash, payWithNagad } = usePayment();
  const [loading, setLoading] = useState(false);
  const [payMethod, setPayMethod] = useState('cod');
  const [addr, setAddr] = useState({ name: '', phone: '', line1: '', line2: '', city: '', district: '', zip: '' });

  const handleChange = e => setAddr(a => ({ ...a, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!addr.name || !addr.phone || !addr.line1 || !addr.city) {
      return toast.error('Please fill in all required fields');
    }
    setLoading(true);
    try {
      const order = await createOrder({
        orderItems: cart.map(i => ({
          product: i._id, name: i.name, image: i.images?.[0],
          size: i.size, quantity: i.quantity, price: i.salePrice || i.price,
        })),
        shippingAddress: addr,
        paymentMethod: payMethod,
        itemsPrice:    cartTotal,
        shippingPrice: shippingFee,
        totalPrice:    cartTotal + shippingFee,
      });

      if (payMethod === 'bkash') {
        localStorage.setItem('yamass_pending_order', order.data._id);
        await payWithBkash(order.data._id);
        return;
      }

      if (payMethod === 'nagad') {
        localStorage.setItem('yamass_pending_order', order.data._id);
        await payWithNagad(order.data._id);
        return;
      }

      clearCart();
      toast.success('Order placed successfully!');
      navigate(`/orders/${order.data._id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to place order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-display text-4xl font-bold text-yamass-black mb-10">Checkout</h1>
      <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-10">

        {/* Left: Address + Payment */}
        <div className="lg:col-span-2 space-y-8">

          {/* Shipping address */}
          <div className="bg-yamass-cream rounded-2xl p-6">
            <h2 className="font-semibold text-yamass-black mb-5">Shipping Address</h2>
            <div className="grid grid-cols-2 gap-4">
              {[
                { name: 'name',     label: 'Full Name *',       col: 1 },
                { name: 'phone',    label: 'Phone Number *',    col: 1 },
                { name: 'line1',    label: 'Address Line 1 *',  col: 2 },
                { name: 'line2',    label: 'Address Line 2',    col: 2 },
                { name: 'city',     label: 'City *',            col: 1 },
                { name: 'district', label: 'District',          col: 1 },
                { name: 'zip',      label: 'ZIP Code',          col: 1 },
              ].map(f => (
                <div key={f.name} className={f.col === 2 ? 'col-span-2' : ''}>
                  <label className="block text-xs text-gray-500 mb-1">{f.label}</label>
                  <input
                    name={f.name}
                    value={addr[f.name]}
                    onChange={handleChange}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-yamass-accent bg-white"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Payment method */}
          <div className="bg-yamass-cream rounded-2xl p-6">
            <h2 className="font-semibold text-yamass-black mb-5">Payment Method</h2>
            <div className="grid grid-cols-2 gap-3">
              {paymentMethods.map(m => (
                <button
                  type="button"
                  key={m.value}
                  onClick={() => setPayMethod(m.value)}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                    payMethod === m.value
                      ? 'border-yamass-black bg-white'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl">{m.icon}</span>
                  <span className="text-sm font-medium">{m.label}</span>
                </button>
              ))}
            </div>
            {payMethod !== 'cod' && (
              <p className="text-xs text-gray-400 mt-3">
                You will be redirected to complete {payMethod === 'card' ? 'card' : payMethod} payment after placing your order.
              </p>
            )}
          </div>
        </div>

        {/* Right: Summary */}
        <div className="bg-yamass-cream rounded-2xl p-6 h-fit sticky top-24">
          <h2 className="font-display text-xl font-bold text-yamass-black mb-5">Order Summary</h2>
          <div className="space-y-3 mb-6">
            {cart.map(item => (
              <div key={`${item._id}-${item.size}`} className="flex justify-between text-sm">
                <span className="text-gray-600 line-clamp-1 flex-1 mr-2">
                  {item.name} ({item.size}) ×{item.quantity}
                </span>
                <span className="font-medium whitespace-nowrap">
                  ৳{((item.salePrice || item.price) * item.quantity).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
          <div className="border-t border-gray-200 pt-4 space-y-2 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span><span className="font-medium">৳{cartTotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-gray-600">
              <span>Shipping</span>
              <span className={shippingFee === 0 ? 'text-green-600 font-medium' : 'font-medium'}>
                {shippingFee === 0 ? 'Free' : `৳${shippingFee}`}
              </span>
            </div>
            <div className="flex justify-between font-bold text-base pt-2 border-t border-gray-200">
              <span>Total</span>
              <span>৳{(cartTotal + shippingFee).toLocaleString()}</span>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="mt-6 w-full bg-yamass-black text-white py-4 rounded-full text-sm font-semibold hover:bg-yamass-accent transition-colors disabled:opacity-50"
          >
            {loading ? 'Placing Order...' : 'Place Order'}
          </button>
        </div>
      </form>
    </div>
  );
}
